import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { UnitGroup } from "~/applications/OfferSearch/Domain/UnitGroup";
import { contentStudioClient } from "~/clients/ContentStudio";
import { TimeOfDay } from "~/core/TimeOfDay";
import { SanityImageWithMetadataPayloadSchema } from "~/core/api/Sanity/asset";
import {
  SanityLocalizedFieldSchema,
  localizeField,
  localizedFieldsProjection,
} from "~/core/api/Sanity/localize";
import { buildGroqQuery } from "~/core/api/Sanity/query";
import { AVAILABLE_LOCALES } from "~/core/locale";

const getUnitGroupByIdQuery = (locale: AVAILABLE_LOCALES) =>
  buildGroqQuery(`
  *[
    _type == 'unitGroup'
    && seoSlug.current == $unitGroupSlug
    && loge._ref in (
      *[
        _type == "logePage"
        && seoSlug.current == $hotelSlug
      ][].loge._ref
    )
    && !(_id in path('drafts.**'))
  ][0]
  {
    pmsId{ current },
    seoSlug{ current },
    loge->{
      pmsId{ current },
      name,
      building->{
        address{
          line1,
          line2,
          postalCode,
          city,
          coordinate{ lat, lng }
        }
      },
      "logePolicy": hotelPolicy {
        arrivalTime,
        departureTime
      },
      "logePage": (
        *[_type == "logePage" && references(^._id) && !(_id in path('drafts.**'))][0]{
          seoSlug{ current }
        }
      ),
    },
    name,
    'description': coalesce(description{ ${localizedFieldsProjection(
      locale,
    )} }, description),
    'gallery': [
      ...gallery[]{
        asset->{
          url,
          metadata {
            dimensions {
              height,
              width
            },
          },
        },
        caption{ ${localizedFieldsProjection(locale)} }
      },
      ...loge->gallery[]{
        asset->{
          url,
          metadata {
            dimensions {
              height,
              width
            },
          },
        },
        caption{ ${localizedFieldsProjection(locale)} }
      },
    ],
    numberOfRooms,
    hasKitchen,
    hasLivingRoom,
    hasConvertibleCouch,
    maxCapacity,
    numberOfBathrooms,
    numberOfReducedMobilityBathrooms,
    surface { surfaceMax, surfaceMin },
    highlightConnectingDoor
  }
`);

const UnitGroupBySeoSlugPayloadSchema = z.object({
  pmsId: z.object({ current: z.string() }),
  seoSlug: z.object({ current: z.string() }),
  loge: z.object({
    pmsId: z.object({ current: z.string() }),
    name: z.string(),
    building: z.object({
      address: z.object({
        line1: z.string(),
        line2: z.string().nullable(),
        postalCode: z.string(),
        city: z.string(),
        coordinate: z.object({
          lat: z.number(),
          lng: z.number(),
        }),
      }),
    }),
    logePolicy: z.object({
      arrivalTime: z.string(),
      departureTime: z.string(),
    }),
    logePage: z.object({
      seoSlug: z.object({ current: z.string() }),
    }),
  }),
  name: z.string(),
  description: z.string().or(SanityLocalizedFieldSchema),
  gallery: z.array(
    z.object({
      asset: SanityImageWithMetadataPayloadSchema,
      caption: SanityLocalizedFieldSchema,
    }),
  ),
  numberOfRooms: z.number(),
  hasKitchen: z.boolean(),
  hasLivingRoom: z.boolean(),
  hasConvertibleCouch: z.boolean(),
  maxCapacity: z.number(),
  numberOfBathrooms: z.number(),
  numberOfReducedMobilityBathrooms: z.number(),
  surface: z.object({
    surfaceMin: z.number(),
    surfaceMax: z.number(),
  }),
  highlightConnectingDoor: z.boolean(),
});

type FetchUnitGroupBySeoSlugQuery = {
  locale: AVAILABLE_LOCALES;
  hotelSlug: string;
  unitGroupSlug: string;
};

export const fetchUnitGroupBySeoSlugKey = (
  query: FetchUnitGroupBySeoSlugQuery,
): ["offer-search-unit-group-by-seo-slug", FetchUnitGroupBySeoSlugQuery] => [
  "offer-search-unit-group-by-seo-slug",
  query,
];

export const fetchUnitGroupBySlug = async ({
  locale,
  hotelSlug,
  unitGroupSlug,
}: FetchUnitGroupBySeoSlugQuery): Promise<UnitGroup> => {
  const response = await contentStudioClient.fetch(
    getUnitGroupByIdQuery(locale),
    { hotelSlug, unitGroupSlug },
  );

  const unitGroupPayload = UnitGroupBySeoSlugPayloadSchema.parse(response);

  if (!unitGroupPayload) {
    throw Error(
      `Could not find the unit group with the unit group "${unitGroupSlug}" in the loge "${hotelSlug}"`,
    );
  }

  return {
    id: unitGroupPayload.pmsId.current,
    slug: unitGroupPayload.seoSlug.current,
    loge: {
      id: unitGroupPayload.loge.pmsId.current,
      slug: unitGroupPayload.loge.logePage.seoSlug.current,
      name: unitGroupPayload.loge.name,
      address: {
        line1: unitGroupPayload.loge.building.address.line1,
        ...(unitGroupPayload.loge.building.address.line2 !== null
          ? { line2: unitGroupPayload.loge.building.address.line2 }
          : {}),
        postalCode: unitGroupPayload.loge.building.address.postalCode,
        city: unitGroupPayload.loge.building.address.city,
        coordinate: {
          latitude: unitGroupPayload.loge.building.address.coordinate.lat,
          longitude: unitGroupPayload.loge.building.address.coordinate.lng,
        },
      },
      policy: {
        arrivalTime: TimeOfDay.fromStringTime(
          unitGroupPayload.loge.logePolicy.arrivalTime,
        ).asHours(),
        departureTime: TimeOfDay.fromStringTime(
          unitGroupPayload.loge.logePolicy.departureTime,
        ).asHours(),
      },
    },
    name: unitGroupPayload.name,
    description:
      typeof unitGroupPayload.description === "string"
        ? unitGroupPayload.description
        : localizeField(unitGroupPayload.description, locale),
    images: unitGroupPayload.gallery.map((oneImage) => ({
      ...oneImage,
      caption: localizeField(oneImage.caption, locale),
    })),
    numberOfRooms: unitGroupPayload.numberOfRooms,
    hasKitchen: unitGroupPayload.hasKitchen,
    hasLivingRoom: unitGroupPayload.hasLivingRoom,
    hasConvertibleCouch: unitGroupPayload.hasConvertibleCouch,
    maxCapacity: unitGroupPayload.maxCapacity,
    numberOfBathrooms: unitGroupPayload.numberOfBathrooms,
    numberOfReducedMobilityBathrooms:
      unitGroupPayload.numberOfReducedMobilityBathrooms,
    surface: {
      minimum: unitGroupPayload.surface.surfaceMin,
      maximum: unitGroupPayload.surface.surfaceMax,
    },
    highlightConnectingDoor: unitGroupPayload.highlightConnectingDoor,
  };
};

type FetchUnitGroupBySlugQuery = {
  locale: AVAILABLE_LOCALES;
  hotelSlug: string;
  unitGroupSlug: string;
};

const fetchUserProfileKey = (
  query: FetchUnitGroupBySlugQuery,
): ["offer-search-unit-group-page", FetchUnitGroupBySlugQuery] => [
  "offer-search-unit-group-page",
  query,
];

export const useUnitGroupBySlug = (
  query: FetchUnitGroupBySlugQuery,
  initialData?: UnitGroup,
): { unitGroup: UnitGroup | null } => {
  const { data: unitGroup = null } = useQuery(
    fetchUserProfileKey(query),
    () => fetchUnitGroupBySlug(query),
    {
      initialData,
    },
  );

  return { unitGroup };
};
